interface DesignItem {
  id?: string;
  type?: string;
  text?: unknown;
  fontFamily?: unknown;
  fontSize?: unknown;
  fontWeight?: unknown;
  fontStyle?: unknown;
  letterSpacing?: unknown;
  lineHeight?: unknown;
  textCurve?: unknown;
  textCurveReverse?: unknown;
  textCurveSpacing?: unknown;
  [key: string]: unknown;
}

export interface ArcGlyph {
  ch: string;
  x: number;
  y: number;
  rotation: number;
}

export interface ArcTextLayout {
  width: number;
  height: number;
  /** Dashed guide path that follows only the text's arc segment (viewBox coords). */
  guidePath: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  fontStyle: string;
  glyphs: ArcGlyph[];
}

type Point = { x: number; y: number };

/**
 * Outline of a glyph's ink in the frame it is drawn in, relative to its point on the arc.
 *
 * A convex hull rather than a box: on the arc a glyph is rotated, and the corners of an 'A' or a
 * 'W' box carry no ink, so a box would claim a reach the letter does not have and tilt the run to
 * balance a phantom.
 */
type GlyphMetric = {
  ch: string;
  advance: number;
  hull: readonly Point[];
};

function n(v: unknown, fallback: number): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
}

function s(v: unknown, fallback: string): string {
  const x = String(v ?? '').trim();
  return x ? x : fallback;
}

function normalizeText(value: unknown): string {
  return String(value ?? '').normalize('NFC').replace(/\s+/g, ' ').trim();
}

/**
 * User-perceived characters. Code points would split a decomposed "ế" or an emoji ZWJ sequence
 * into separate `<text>` nodes that each rotate on their own and draw detached marks.
 */
function graphemes(text: string): string[] {
  const Segmenter = (Intl as { Segmenter?: new (locale?: string, options?: { granularity: 'grapheme' }) => { segment(input: string): Iterable<{ segment: string }> } }).Segmenter;
  if (!Segmenter) return Array.from(text);
  return Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text), (part) => part.segment);
}

function clampCurve(value: unknown): number {
  const x = n(value, 0);
  return Math.max(-100, Math.min(100, x));
}

function fontShorthandAt(item: DesignItem, fontSize: number): string {
  return `${s(item.fontStyle, 'normal')} ${s(item.fontWeight, 'normal')} ${fontSize}px ${s(item.fontFamily, 'Arial')}`;
}

function buildFontShorthand(item: DesignItem): string {
  return fontShorthandAt(item, n(item.fontSize, 24));
}

function isFontReady(fontFace: string): boolean {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (typeof fonts?.check !== 'function') return true;
  try {
    return fonts.check(fontFace);
  } catch {
    return true;
  }
}

/** Em size the outline is traced at: fine enough that a hull vertex lands well inside a pixel. */
const HULL_EM = 64;
const hullCache = new Map<string, readonly Point[] | null>();

/** Lower hull then upper hull (monotone chain), which is all the reach queries need. */
function convexHull(points: Point[]): Point[] {
  if (points.length < 4) return points;
  const sorted = [...points].sort((a, b) => (a.x - b.x) || (a.y - b.y));
  const cross = (o: Point, a: Point, b: Point) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const build = (input: Point[]) => {
    const chain: Point[] = [];
    for (const point of input) {
      while (chain.length >= 2 && cross(chain[chain.length - 2], chain[chain.length - 1], point) <= 0) chain.pop();
      chain.push(point);
    }
    return chain;
  };
  const lower = build(sorted);
  const upper = build([...sorted].reverse());
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

/**
 * Convex hull of a glyph's painted pixels, in em units around its baseline-left drawing origin.
 *
 * Traced once per font and character: the outline is what decides how far a rotated glyph reaches,
 * and canvas hands out nothing but an axis-aligned box.
 */
function traceGlyphHull(ch: string, fontFace: string, fontReady: boolean): readonly Point[] | null {
  // Readiness is part of the key so a hull traced from the fallback face during a webfont's FOUT
  // is retraced once the real face lands, instead of pinning the box to the wrong outline.
  const key = `${fontFace}\u0000${fontReady ? 1 : 0}\u0000${ch}`;
  const cached = hullCache.get(key);
  if (cached !== undefined) return cached;

  const traced = (() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext?.('2d');
    if (!ctx || typeof ctx.getImageData !== 'function' || typeof ctx.fillText !== 'function') return null;
    const originX = Math.round(HULL_EM * 0.75);
    const originY = Math.round(HULL_EM * 1.75);
    canvas.width = HULL_EM * 3;
    canvas.height = HULL_EM * 3;
    ctx.font = fontFace;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#000';
    ctx.fillText(ch, originX, originY);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const points: Point[] = [];
    for (let y = 0; y < canvas.height; y += 1) {
      let first = -1;
      let last = -1;
      for (let x = 0; x < canvas.width; x += 1) {
        if (data[(y * canvas.width + x) * 4 + 3] <= 32) continue;
        if (first < 0) first = x;
        last = x;
      }
      if (first < 0) continue;
      // Only the extremes of a row can be hull vertices, and both pixel edges count so the hull
      // covers the painted area rather than the pixel centres.
      const top = (y - originY) / HULL_EM;
      const bottom = (y + 1 - originY) / HULL_EM;
      const left = (first - originX) / HULL_EM;
      const right = (last + 1 - originX) / HULL_EM;
      points.push({ x: left, y: top }, { x: left, y: bottom }, { x: right, y: top }, { x: right, y: bottom });
    }
    return points.length ? convexHull(points) : null;
  })();

  hullCache.set(key, traced);
  return traced;
}

function boxHull(halfWidth: number, halfHeight: number): Point[] {
  return [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: -halfWidth, y: halfHeight },
  ];
}

/** Axis-aligned ink box of a glyph before it is rotated onto the arc. */
function hullBox(hull: readonly Point[]) {
  return hull.reduce(
    (box, point) => ({
      left: Math.min(box.left, point.x),
      right: Math.max(box.right, point.x),
      top: Math.min(box.top, point.y),
      bottom: Math.max(box.bottom, point.y),
    }),
    { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity },
  );
}

function measureGlyphs(text: string, item: DesignItem): GlyphMetric[] {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const fontSize = n(item.fontSize, 24);
  const spacing = n(item.letterSpacing, 0) + n(item.textCurveSpacing, 0);
  // The em-box fallback stands in for real ink whenever the platform withholds glyph metrics.
  const fallbackHalfHeight = Math.max(1, fontSize * n(item.lineHeight, 1.2)) / 2;

  if (!ctx) {
    return graphemes(text).map((ch) => {
      const blank = !ch.trim();
      const width = blank ? fontSize * 0.33 : fontSize * 0.6;
      return {
        ch,
        advance: Math.max(0, width + spacing),
        hull: blank ? [] : boxHull(width / 2, fallbackHalfHeight),
      };
    });
  }

  ctx.font = buildFontShorthand(item);
  const xHeight = Number(ctx.measureText('x').actualBoundingBoxAscent);
  // `dominant-baseline="middle"` sits the anchor half an x-height above the alphabetic baseline.
  const halfXHeight = Number.isFinite(xHeight) ? xHeight / 2 : 0;
  const fontFace = fontShorthandAt(item, HULL_EM);
  const fontReady = isFontReady(fontFace);
  const chars = graphemes(text);
  // Advances come from the growth of the measured prefix, not from measuring each character on
  // its own: that keeps the kerning pairs the font asks for ('Ta', 'AV'), so letter gaps on the
  // arc match what straight text — and Canva's curve — would show.
  let prefix = 0;
  return chars.map((ch, index) => {
    const nextPrefix = ctx.measureText(chars.slice(0, index + 1).join('')).width;
    const kerned = nextPrefix - prefix;
    prefix = nextPrefix;
    const width = ctx.measureText(ch).width;
    const advance = Number.isFinite(kerned) && kerned > 0 ? kerned : width;
    const traced = ch.trim() ? traceGlyphHull(ch, fontFace, fontReady) : null;
    // `text-anchor="middle"` centres the glyph on its own advance, so the traced outline moves back
    // half of it to sit around the point on the arc.
    let hull: readonly Point[] = [];
    if (traced) {
      hull = traced.map((point) => ({ x: point.x * fontSize - width / 2, y: point.y * fontSize + halfXHeight }));
    } else if (ch.trim()) {
      hull = inkHullFromMetrics(ctx.measureText(ch), width, halfXHeight, fallbackHalfHeight);
    }
    return { ch, advance: Math.max(0, advance + spacing), hull };
  });
}

/** Box fallback for platforms that will not rasterize: the glyph's own metrics, else the em box. */
function inkHullFromMetrics(
  metrics: TextMetrics,
  advanceWidth: number,
  middleShift: number,
  fallbackHalfHeight: number,
): Point[] {
  const left = Number(metrics.actualBoundingBoxLeft);
  const right = Number(metrics.actualBoundingBoxRight);
  const ascent = Number(metrics.actualBoundingBoxAscent);
  const descent = Number(metrics.actualBoundingBoxDescent);
  if (![left, right, ascent, descent].every((value) => Number.isFinite(value))) {
    return boxHull(advanceWidth / 2, fallbackHalfHeight);
  }
  const x0 = -left - advanceWidth / 2;
  const x1 = right - advanceWidth / 2;
  const y0 = -ascent + middleShift;
  const y1 = descent + middleShift;
  return [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];
}

type PlacedGlyph = ArcGlyph & { hull: readonly Point[] };

type ArcFrame = {
  radiusPx: number;
  dir: 1 | -1;
  baseAngle: number;
  circleCenterY: number;
};

function inkCorners(glyph: PlacedGlyph): Point[] {
  const r = (glyph.rotation * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  return glyph.hull.map((point) => ({
    x: glyph.x + point.x * cos - point.y * sin,
    y: glyph.y + point.x * sin + point.y * cos,
  }));
}

function includeInk(
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  glyph: PlacedGlyph,
) {
  inkCorners(glyph).forEach((corner) => {
    bounds.minX = Math.min(bounds.minX, corner.x);
    bounds.maxX = Math.max(bounds.maxX, corner.x);
    bounds.minY = Math.min(bounds.minY, corner.y);
    bounds.maxY = Math.max(bounds.maxY, corner.y);
  });
}

/** How far a glyph's ink reaches away from the arc apex, along the axis the arc bends towards. */
function outwardDepth(glyph: PlacedGlyph, dir: 1 | -1): number {
  return inkCorners(glyph).reduce((depth, corner) => Math.max(depth, dir * corner.y), -Infinity);
}

function placeGlyph(
  metric: GlyphMetric,
  slotMid: number,
  runCenter: number,
  frame: ArcFrame,
): PlacedGlyph {
  const { radiusPx, dir, baseAngle, circleCenterY } = frame;
  const theta = baseAngle + dir * ((slotMid - runCenter) / radiusPx);
  return {
    ch: metric.ch,
    x: radiusPx * Math.cos(theta),
    y: circleCenterY + radiusPx * Math.sin(theta),
    rotation: (theta * 180) / Math.PI + (dir === 1 ? 90 : -90),
    hull: metric.hull,
  };
}

/**
 * Arc-length offset of the apex that leaves both ends of the run equally deep.
 *
 * Centring on the advance slots — or on the midpoint of the outer glyph centres — cannot balance
 * the run, because at the ends of an arc a glyph is rotated by roughly a quarter turn and its
 * *width* becomes its vertical reach: a wide 'A' dives far below a narrow 't', so the box bottom
 * hugs the 'A' and the 't' keeps a visible gap that swaps sides with the reverse toggle. Seeding
 * on the outer ink edges cancels most of that, and a short bisection inside half an end glyph
 * closes the rest without letting the run drift off centre.
 */
function balancedRunCenter(
  metrics: GlyphMetric[],
  slotMids: number[],
  frame: ArcFrame,
): number {
  const lastIndex = metrics.length - 1;
  const slotCenter = (slotMids[0] + slotMids[lastIndex]) / 2;
  const first = metrics[0];
  const last = metrics[lastIndex];
  if (lastIndex <= 0 || !first.hull.length || !last.hull.length) return slotCenter;

  const firstBox = hullBox(first.hull);
  const lastBox = hullBox(last.hull);
  const seed = (slotMids[0] + firstBox.left + slotMids[lastIndex] + lastBox.right) / 2;
  const imbalance = (runCenter: number) =>
    outwardDepth(placeGlyph(first, slotMids[0], runCenter, frame), frame.dir) -
    outwardDepth(placeGlyph(last, slotMids[lastIndex], runCenter, frame), frame.dir);

  const reach = (firstBox.right - firstBox.left + lastBox.right - lastBox.left) / 4;
  let low = seed - reach;
  let high = seed + reach;
  const lowValue = imbalance(low);
  if ((lowValue > 0) === (imbalance(high) > 0)) {
    return [seed, low, high].reduce((best, candidate) =>
      Math.abs(imbalance(candidate)) < Math.abs(imbalance(best)) ? candidate : best);
  }

  for (let iteration = 0; iteration < 24; iteration += 1) {
    const middle = (low + high) / 2;
    if ((imbalance(middle) > 0) === (lowValue > 0)) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
}

export function measureArcTextLayout(item: DesignItem): ArcTextLayout | null {
  const text = normalizeText(item.text);
  const curveAmount = clampCurve(item.textCurve);
  if (!text || curveAmount === 0) return null;

  const signDir: 1 | -1 = curveAmount < 0 ? -1 : 1;
  const dir: 1 | -1 = item.textCurveReverse ? (-signDir as 1 | -1) : signDir;
  const fontFamily = s(item.fontFamily, 'Arial');
  const fontSize = n(item.fontSize, 24);
  const fontWeight = s(item.fontWeight, 'normal');
  const fontStyle = s(item.fontStyle, 'normal');
  const metrics = measureGlyphs(text, item);
  const totalLen = metrics.reduce((sum, metric) => sum + metric.advance, 0);
  if (totalLen <= 0) return null;

  const totalAngle = (Math.abs(curveAmount) / 100) * Math.PI * 2;
  const radiusPx = totalLen / Math.max(0.001, totalAngle);
  const frame: ArcFrame = {
    radiusPx,
    dir,
    baseAngle: dir === 1 ? -Math.PI / 2 : Math.PI / 2,
    circleCenterY: dir * radiusPx,
  };
  const slotMids: number[] = [];
  let cum = 0;
  metrics.forEach((metric) => {
    slotMids.push(cum + metric.advance / 2);
    cum += metric.advance;
  });
  const runCenter = balancedRunCenter(metrics, slotMids, frame);
  const bounds = {
    minX: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  };

  const rawGlyphs = metrics.map((metric, index) =>
    placeGlyph(metric, slotMids[index], runCenter, frame));
  // Blank glyphs (spaces) carry an advance but no ink, so letting them vote on the box would
  // stretch it past anything the eye can see.
  rawGlyphs.forEach((glyph) => {
    if (glyph.hull.length) includeInk(bounds, glyph);
  });
  if (!Number.isFinite(bounds.minY)) return null;

  // Bounds hug only the painted arc segment (Canva-style) — no longer the full circle — with the
  // same padding on all four sides. Squaring the box around the apex instead would centre the apex
  // but leave the run itself off centre, and that phantom margin reads as the same lopsidedness the
  // balance solve exists to remove.
  const padding = Math.max(4, fontSize * 0.12);
  const width = Math.max(1, bounds.maxX - bounds.minX + padding * 2);
  const height = Math.max(1, bounds.maxY - bounds.minY + padding * 2);
  const offsetX = -bounds.minX + padding;
  const offsetY = -bounds.minY + padding;
  const glyphs = rawGlyphs.map((glyph) => ({
    ch: glyph.ch,
    x: glyph.x + offsetX,
    y: glyph.y + offsetY,
    rotation: glyph.rotation,
  }));

  // Dashed guide that follows just the text's arc segment (passes through glyph centers), running
  // from the first glyph's outer ink edge to the last one's so it covers exactly what is painted.
  const lastIndex = metrics.length - 1;
  const startOffset = slotMids[0] + hullBox(metrics[0].hull).left - runCenter;
  const endOffset = slotMids[lastIndex] + hullBox(metrics[lastIndex].hull).right - runCenter;
  const startTheta = frame.baseAngle + dir * (startOffset / radiusPx);
  const endTheta = frame.baseAngle + dir * (endOffset / radiusPx);
  const arcPoint = (theta: number) => ({
    x: radiusPx * Math.cos(theta) + offsetX,
    y: frame.circleCenterY + radiusPx * Math.sin(theta) + offsetY,
  });
  const p0 = arcPoint(startTheta);
  const p1 = arcPoint(endTheta);
  const largeArcFlag = Math.abs(endTheta - startTheta) > Math.PI ? 1 : 0;
  const sweepFlag = endTheta > startTheta ? 1 : 0;
  const guidePath =
    `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} ` +
    `A ${radiusPx.toFixed(2)} ${radiusPx.toFixed(2)} 0 ${largeArcFlag} ${sweepFlag} ` +
    `${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;

  return {
    width,
    height,
    guidePath,
    fontFamily,
    fontSize,
    fontWeight,
    fontStyle,
    glyphs,
  };
}

export function measureArcTextBounds(item: DesignItem): { width: number; height: number } | null {
  const layout = measureArcTextLayout(item);
  return layout ? { width: layout.width, height: layout.height } : null;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Shared arc SVG body for preview renderer and cart/print export. */
export function buildArcSvgFragment(item: DesignItem): string {
  const layout = measureArcTextLayout(item);
  if (!layout) return '';

  const fill = escapeXml(String(item.color ?? '#000'));
  const opacity = Number.isFinite(Number(item.opacity)) ? Number(item.opacity) : 1;
  const glyphs = layout.glyphs
    .map((glyph) => {
      const ch = escapeXml(glyph.ch);
      return `<text x="${glyph.x}" y="${glyph.y}" text-anchor="middle" dominant-baseline="middle" transform="rotate(${glyph.rotation} ${glyph.x} ${glyph.y})">${ch}</text>`;
    })
    .join('');

  return [
    `<g fill="${fill}" opacity="${opacity}" font-family="${escapeXml(layout.fontFamily)}" font-size="${layout.fontSize}" font-weight="${escapeXml(layout.fontWeight)}" font-style="${escapeXml(layout.fontStyle)}">`,
    glyphs,
    '</g>',
  ].join('');
}

/**
 * Standalone arc SVG for export.
 *
 * `fontFaceCss` must be supplied whenever the markup is rasterized outside the editor
 * page (canvas thumbnails, raw SVG download, server-composed export): a `data:` URL SVG
 * loaded as an image cannot reach the host document's webfonts, so without the inlined
 * face the glyphs fall back to a system font. `buildArcSvgFragment` stays bare because
 * the live DOM renderer inherits the page's fonts.
 */
export function buildArcSvgString(
  item: DesignItem,
  options: { fontFaceCss?: string } = {},
): string {
  const layout = measureArcTextLayout(item);
  if (!layout) return '';
  const body = buildArcSvgFragment(item);
  const fontFaceCss = String(options.fontFaceCss ?? '').trim();
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${layout.width} ${layout.height}" width="${layout.width}" height="${layout.height}">`,
    fontFaceCss ? `<style>${fontFaceCss}</style>` : '',
    body,
    '</svg>',
  ].join('');
}
